import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useDashboardPrefs, type DashboardSurface } from '@/lib/use-dashboard-prefs';
import {
    cycleSize,
    resolveSize,
    type StoredWidgetEntry,
    type WidgetHeight,
    type WidgetKind,
    type WidgetSize,
    type WidgetSizeSpec,
    type WidgetWidth,
} from '@/components/my-page/grid/widget-sizes';

export type WidgetId =
    // Desk>My Page — the signed-in IM's own live KPI slice. Every figure is the
    // caller's own book; tiles also carry "my % of firm" against firm totals.
    // KPIs are computed live from the ValuationGrid (AUM/counts/YTD); flows,
    // income and performance have no live source (see docs/kpi-gaps.md).
    | 'desk_my_page_aum_tile'
    // My AUA tile (Advisory + Execution-Only) — companion to My AUM (Discretionary)
    | 'desk_my_page_aua_tile'
    | 'desk_my_page_ytd_tile'
    | 'desk_my_page_clients_tile'
    | 'desk_my_page_portfolios_tile'
    // My per-category breakdown bars
    | 'desk_my_page_aum_by_category'
    // Across-month AUM trend (accrues from each ValuationGrid ingest)
    | 'desk_my_page_aum_trend'
    // Donut + my-categories table
    | 'desk_my_page_category_donut'
    | 'desk_my_page_categories_table'
    // Comply — synthetic demo widget catalogue (Comply>My Page)
    | 'comply_breach_status'
    | 'comply_kyc_expiry'
    | 'comply_tc_completion'
    | 'comply_consumer_duty'
    | 'comply_smcr_status'
    // View>KPI — firm-wide live KPI widget catalogue.
    | 'view_kpi_aum_tile'
    | 'view_kpi_ytd_tile'
    | 'view_kpi_clients_tile'
    | 'view_kpi_portfolios_tile'
    // Per-IM breakdown bars
    | 'view_kpi_aum_by_im'
    | 'view_kpi_aum_by_category'
    // Across-month AUM trend
    | 'view_kpi_aum_trend'
    // Donut + league table
    | 'view_kpi_category_donut'
    | 'view_kpi_league';

export type Frontend = 'desk' | 'ops' | 'comply' | 'view';

export interface PinnedWidget {
    id: WidgetId;
    label: string;
    frontend: Frontend;
    pinnedAt: number;
    /**
     * The widget's footprint on the flow grid, in grid cells (issue #215). Both
     * are optional: a pin minted before a size was chosen, or rehydrated from a
     * legacy saved layout, carries no `w`/`h` and falls back to its kind's
     * catalogue default at render (`resolveSize`).
     */
    w?: WidgetWidth;
    h?: WidgetHeight;
}

/**
 * A widget id paired with its persisted footprint — the shape that survives the
 * round-trip through the prefs API. `id` is narrowed to a known `WidgetId`; the
 * generic `StoredWidgetEntry` (off the wire) keeps `id` a bare string.
 */
export interface PinnedEntry {
    id: WidgetId;
    w?: WidgetWidth;
    h?: WidgetHeight;
}

/**
 * Per-frontend dashboard surface key (ADR-0009). Maps the in-app `frontend`
 * the pins are grouped by onto the opaque persistence `surface` the server
 * keys layouts on. Only the surfaces in the server's allow-list persist; a
 * frontend absent here keeps its pins in memory only (forward-compatible —
 * #190/#191 extend the catalogue and the allow-list together).
 */
const FRONTEND_SURFACE: Partial<Record<Frontend, DashboardSurface>> = {
    desk: 'desk_my_page',
    view: 'view_kpi',
};

/**
 * Per-surface default-pinned widget set (ADR-0009 / PRD #185).
 *
 * Applied only on the `present: false` path — when the signed-in user has never
 * saved a layout for this surface, these ids are the starting pin set so the
 * page is useful out of the box. Once the user pins / unpins anything the saved
 * row takes over (the surface is then `present: true` on every later load and
 * this default is never re-applied). Exported so it can be unit-tested.
 */
export const SURFACE_DEFAULT_WIDGETS: Partial<Record<DashboardSurface, WidgetId[]>> = {
    view_kpi: [
        'view_kpi_aum_tile',
        'view_kpi_ytd_tile',
        'view_kpi_clients_tile',
        'view_kpi_league',
    ],
    desk_my_page: [
        'desk_my_page_aum_tile',
        'desk_my_page_ytd_tile',
        'desk_my_page_clients_tile',
        'desk_my_page_categories_table',
    ],
};

/**
 * Resolve the sized entry list a surface should start from on first load: the
 * server-saved order (with each entry's persisted footprint) when the user has
 * a row, otherwise the surface default (ids only — every default widget renders
 * at its kind's catalogue size). Pure so the default-vs-saved branch is
 * unit-testable without a DOM.
 */
export function resolveInitialEntries(
    surface: DashboardSurface,
    present: boolean,
    savedEntries: ReadonlyArray<StoredWidgetEntry>,
): PinnedEntry[] {
    if (present) {
        return savedEntries.map((e) => ({
            id: e.id as WidgetId,
            w: e.w as WidgetWidth | undefined,
            h: e.h as WidgetHeight | undefined,
        }));
    }
    return (SURFACE_DEFAULT_WIDGETS[surface] ?? []).map((id) => ({ id }));
}

/**
 * Pure reducer: replace one frontend's pins with the server-saved entry order.
 *
 * This is the heart of reload-survival (ADR-0009) — when a remounted provider
 * loads a saved layout, this rebuilds that frontend's slice of the pin set from
 * the ordered entries, carrying each entry's persisted footprint (`w`/`h`) and
 * reusing any matching in-memory pin's LABEL. Pins on other frontends are
 * untouched. Exported so it can be unit-tested without a DOM.
 */
export function hydrateFrontendPins(
    prev: PinnedWidget[],
    frontend: Frontend,
    entries: ReadonlyArray<PinnedEntry>,
): PinnedWidget[] {
    const others = prev.filter((w) => w.frontend !== frontend);
    const rehydrated: PinnedWidget[] = entries.map((entry, i) => {
        const existing = prev.find((w) => w.id === entry.id);
        return {
            id: entry.id,
            label: existing?.label ?? entry.id,
            frontend,
            pinnedAt: existing?.pinnedAt ?? i,
            w: entry.w,
            h: entry.h,
        };
    });
    return [...others, ...rehydrated];
}

/**
 * Pure reorder reducer: move one frontend's pin to a new position relative to
 * another pin on the SAME frontend, leaving every other frontend's pins (and
 * their interleaving in the global list) untouched.
 *
 * This is the heart of drag-to-reorder (issue #217). The grid drives it with
 * the dragged pin's id (`activeId`) and the pin it was dropped over (`overId`);
 * we reorder only this frontend's slice via `arrayMove` semantics, then splice
 * the reordered slice back into the global list IN PLACE — i.e. each global
 * position that held one of this frontend's pins now holds the next pin from
 * the reordered slice. Array order is what persists (the `SurfaceSync` mirror
 * writes it back debounced), so this is also the persist-on-reorder seam.
 *
 * No-ops (unknown id, same id, or a cross-frontend drop) return `prev`
 * unchanged so the caller can skip a redundant write. Exported for unit tests.
 */
export function reorderFrontendPins(
    prev: PinnedWidget[],
    frontend: Frontend,
    activeId: WidgetId,
    overId: WidgetId,
): PinnedWidget[] {
    if (activeId === overId) return prev;
    const slice = prev.filter((w) => w.frontend === frontend);
    const from = slice.findIndex((w) => w.id === activeId);
    const to = slice.findIndex((w) => w.id === overId);
    if (from === -1 || to === -1) return prev;

    const reordered = [...slice];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    // Splice the reordered slice back into the global list, position-for-position
    // over the slots this frontend's pins occupied, leaving other frontends fixed.
    let i = 0;
    return prev.map((w) => (w.frontend === frontend ? reordered[i++] : w));
}

/**
 * Pure resize reducer: step ONE pin to the next preset in its size-spec cycle
 * (issue #216). The pin's current footprint is resolved against its spec (which
 * also re-fits a legacy-default pin) for any dimension it omits (`resolveSize`),
 * then advanced via `cycleSize`; the new `w`/`h` replace the pin's footprint in
 * place. `kind` is passed alongside `spec` because `resolveSize`'s legacy-default
 * re-fit is keyed on the kind.
 *
 * Mirrors `reorderFrontendPins`: array identity is what the `SurfaceSync` mirror
 * persists (the stored entry already carries `w`/`h`), so writing the new size
 * onto the pin IS the persist-on-resize seam — no new persistence code. An
 * unknown id is a no-op (returns `prev`) so the caller can skip a redundant
 * write. Exported for unit tests.
 */
export function resizeFrontendPin(
    prev: PinnedWidget[],
    id: WidgetId,
    spec: WidgetSizeSpec,
    kind: WidgetKind,
): PinnedWidget[] {
    const target = prev.find((w) => w.id === id);
    if (!target) return prev;
    const next = cycleSize(spec, resolveSize({ id, w: target.w, h: target.h }, spec, kind));
    return prev.map((w) => (w.id === id ? { ...w, w: next.w, h: next.h } : w));
}

/**
 * The new widget a library-source drag drops onto the canvas: its id + label,
 * its frontend, and its resolved default footprint (the caller resolves the
 * widget's size spec — per-widget override over kind fallback — to a concrete
 * `size`, mirroring `buildPinPayload`). `index` is the position within the
 * target frontend's slice the drop resolves to (the slot of the card it was
 * dropped over; the slice length appends at the end).
 */
export interface InsertSpec {
    id: WidgetId;
    label: string;
    size: WidgetSize;
    frontend: Frontend;
    pinnedAt: number;
    index: number;
}

/**
 * Pure insert-at-index reducer: drop a library widget onto the canvas at a
 * chosen position (issue #219). This is the placement counterpart to
 * `reorderFrontendPins` — where reorder moves an existing pin, this inserts a
 * NEW pin into the target frontend's slice at `index`, at the resolved default
 * footprint the caller put on `spec.size`.
 *
 * The insert is guarded against duplicates: a widget already pinned ANYWHERE
 * returns `prev` unchanged (consistent with tap-to-add, which disables an
 * already-pinned card), so a drag from a pinned library card can never mint a
 * second copy. `index` is clamped into `[0, sliceLength]`; the new pin is
 * spliced into the global list at the absolute position of the frontend slot it
 * resolves to (appending after this frontend's last pin when `index` is the
 * slice length), leaving other frontends' pins fixed.
 *
 * Array order is what persists (the `SurfaceSync` mirror writes it back
 * debounced), so this insert IS the persist-on-drop seam — the new pin's
 * footprint rides along. Exported for unit tests.
 */
export function insertFrontendPinAt(prev: PinnedWidget[], spec: InsertSpec): PinnedWidget[] {
    if (prev.some((w) => w.id === spec.id)) return prev;

    const newPin: PinnedWidget = {
        id: spec.id,
        label: spec.label,
        frontend: spec.frontend,
        pinnedAt: spec.pinnedAt,
        w: spec.size.w,
        h: spec.size.h,
    };

    // Absolute positions of this frontend's existing pins in the global list.
    const slots = prev.reduce<number[]>((acc, w, i) => {
        if (w.frontend === spec.frontend) acc.push(i);
        return acc;
    }, []);
    const clamped = Math.max(0, Math.min(spec.index, slots.length));
    // Insert before the slot at `clamped`; at the slice end, insert right after
    // this frontend's last pin (or at the global end when it has none).
    const at =
        clamped < slots.length
            ? slots[clamped]
            : slots.length > 0
              ? slots[slots.length - 1] + 1
              : prev.length;

    const next = [...prev];
    next.splice(at, 0, newPin);
    return next;
}

interface MyPageContextValue {
    pinned: PinnedWidget[];
    pin: (widget: PinnedWidget) => void;
    unpin: (id: WidgetId) => void;
    isPinned: (id: WidgetId) => boolean;
    /**
     * Reorder one frontend's pins: move `activeId` to where `overId` sits
     * (drag-to-reorder, issue #217). The new array order persists through the
     * same debounced `SurfaceSync` mirror that pin/unpin use.
     */
    reorder: (frontend: Frontend, activeId: WidgetId, overId: WidgetId) => void;
    /**
     * Step one pin to the next preset in its size-spec cycle (resize control,
     * issue #216). `spec` is the widget's resolved size spec (per-widget
     * override over kind fallback); `kind` keys `resolveSize`'s legacy-default
     * re-fit. The new footprint persists through the same debounced `SurfaceSync`
     * mirror that pin/unpin/reorder use.
     */
    resize: (id: WidgetId, spec: WidgetSizeSpec, kind: WidgetKind) => void;
    /**
     * Insert a NEW pin from the library at a chosen position on the canvas
     * (drag-from-library placement, issue #219). The new pin lands at its
     * kind's catalogue-default size; an already-pinned id is a no-op (no
     * duplicate). Order persists through the same debounced `SurfaceSync`
     * mirror that pin/unpin/reorder/resize use.
     */
    insert: (spec: InsertSpec) => void;
}

const MyPageContext = createContext<MyPageContextValue | null>(null);

/**
 * One persisted surface's slice of the global pin set. Mounted once per
 * frontend that has a backing surface; loads that surface's saved widget-id
 * order on mount and writes it back (debounced) whenever this frontend's pins
 * change. It owns no rendered UI — it is a persistence sidecar that keeps the
 * server row in sync with the in-memory `pinned` list.
 */
function SurfaceSync({
    frontend,
    surface,
    pinned,
    onHydrate,
}: {
    frontend: Frontend;
    surface: DashboardSurface;
    pinned: PinnedWidget[];
    onHydrate: (frontend: Frontend, entries: PinnedEntry[]) => void;
}) {
    const { state, setWidgets } = useDashboardPrefs(surface);

    // On first server load, hydrate the in-memory pin set for this frontend.
    // A saved row hydrates its id order (reload survival); an absent row
    // (`present: false`) hydrates the surface default so the page is useful out
    // of the box. After hydration the mirror effect below writes the resolved
    // ids back, materialising the default as the user's starting layout.
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (!state.loaded || hydratedRef.current) return;
        hydratedRef.current = true;
        const initial = resolveInitialEntries(surface, state.present, state.widgets);
        if (initial.length > 0) {
            onHydrate(frontend, initial);
        }
    }, [state.loaded, state.present, state.widgets, surface, frontend, onHydrate]);

    // After hydration, mirror every change to this frontend's pins back to the
    // server as the ordered sized-entry list.
    const entries = useMemo(() => entriesFor(pinned, frontend), [pinned, frontend]);
    // Stable serialisation including w/h, so a reorder OR a resize triggers a
    // write but an otherwise-unchanged render does not.
    const entriesKey = entries.map((e) => `${e.id}:${e.w ?? ''}:${e.h ?? ''}`).join(' ');
    useEffect(() => {
        if (!hydratedRef.current) return;
        setWidgets(entries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesKey]);

    return null;
}

/** Serialise this frontend's pins to the ordered sized entry list to persist. */
function entriesFor(pinned: PinnedWidget[], frontend: Frontend): StoredWidgetEntry[] {
    return pinned
        .filter((w) => w.frontend === frontend)
        .map((w) => ({
            id: w.id,
            ...(w.w !== undefined ? { w: w.w } : {}),
            ...(w.h !== undefined ? { h: w.h } : {}),
        }));
}

export function MyPageProvider({ children }: { children: ReactNode }) {
    const [pinned, setPinned] = useState<PinnedWidget[]>([]);

    const pin = (widget: PinnedWidget) => {
        setPinned((prev) => (prev.find((p) => p.id === widget.id) ? prev : [...prev, widget]));
    };

    const unpin = (id: WidgetId) => {
        setPinned((prev) => prev.filter((p) => p.id !== id));
    };

    const isPinned = (id: WidgetId) => pinned.some((p) => p.id === id);

    const reorder = (frontend: Frontend, activeId: WidgetId, overId: WidgetId) => {
        setPinned((prev) => reorderFrontendPins(prev, frontend, activeId, overId));
    };

    const resize = (id: WidgetId, spec: WidgetSizeSpec, kind: WidgetKind) => {
        setPinned((prev) => resizeFrontendPin(prev, id, spec, kind));
    };

    const insert = (spec: InsertSpec) => {
        setPinned((prev) => insertFrontendPinAt(prev, spec));
    };

    // Replace a frontend's pins with the server-saved sized-entry order on
    // hydration. Labels are not persisted (the server stores ids + sizes only);
    // a placeholder label is carried until the consuming page's widget catalogue
    // resolves the real one by id (MyPageShell rehydrates labels from its
    // config). Each entry's footprint (w/h) carries through.
    const hydrate = (frontend: Frontend, entries: PinnedEntry[]) => {
        setPinned((prev) => hydrateFrontendPins(prev, frontend, entries));
    };

    return (
        <MyPageContext.Provider value={{ pinned, pin, unpin, isPinned, reorder, resize, insert }}>
            {Object.entries(FRONTEND_SURFACE).map(([frontend, surface]) => (
                <SurfaceSync
                    key={frontend}
                    frontend={frontend as Frontend}
                    surface={surface}
                    pinned={pinned}
                    onHydrate={hydrate}
                />
            ))}
            {children}
        </MyPageContext.Provider>
    );
}

export function useMyPage() {
    const ctx = useContext(MyPageContext);
    if (!ctx) throw new Error('useMyPage must be used within MyPageProvider');
    return ctx;
}
